import React, { useState } from 'react';
import { Alert, Button, Col, Form, Row, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import PageForm from '../../components/layout/PageForm';
import PageShell from '../../components/layout/PageShell';
import PaginatedAsyncSelect from '../../components/form/PaginatedAsyncSelect';
import NumberInput from '../../components/form/NumberInput';
import { ProductDimensionsFields } from './components/ProductDimensions';
import { useDefaultDimensionUnit } from './hooks/useDefaultDimensionUnit';
import { EMPTY_PRODUCT_DIMENSIONS, buildProductDimensionsPayload } from './productDimensions';
import type { ProductDimensionsFormData } from './types';

interface SelectOption {
  value: number;
  label: string;
}

interface TaxSetting {
  id: number;
  name: string;
  tax_type: string;
  rate: string;
  is_default: boolean;
  is_active: boolean;
}

interface FormState extends ProductDimensionsFormData {
  name: string;
  sku: string;
  description: string;
  color: string;
  category: number | '';
  category_option: SelectOption | null;
  product_type: number | '';
  product_type_option: SelectOption | null;
  tracking_type: string;
  priority: number;
  weight: string;
  weight_unit: number | '';
  weight_unit_option: SelectOption | null;
  default_daily_rate: string;
  price_purchase: string;
  price_purchase_vat: string;
  currency: number | '';
  currency_option: SelectOption | null;
  exclude_from_discount: boolean;
}

const emptyProduct: FormState = {
  name: '',
  sku: '',
  description: '',
  color: '#066fd1',
  category: '',
  category_option: null,
  product_type: '',
  product_type_option: null,
  tracking_type: 'SERIALIZED',
  priority: 0,
  weight: '0',
  weight_unit: '',
  weight_unit_option: null,
  default_daily_rate: '',
  price_purchase: '',
  price_purchase_vat: '',
  currency: '',
  currency_option: null,
  exclude_from_discount: false,
  ...EMPTY_PRODUCT_DIMENSIONS,
};

export default function ProductAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(emptyProduct);
  const [error, setError] = useState<string | null>(null);
  useDefaultDimensionUnit(form, setForm);

  // Bezpečné načtení daně – vyřešen dvojitý .data unwrapping
  const { data: defaultTax } = useQuery<TaxSetting>({
    queryKey: ['defaultTax'],
    queryFn: async () => {
      const res: any = await api.get('settings/api/tax/default/');
      return res?.data ?? res;
    },
  });

  const mapNamedOption = (entry: { id: number; name: string; parent_name?: string }): SelectOption => ({
    value: entry.id,
    label: entry.parent_name ? `${entry.parent_name} / ${entry.name}` : entry.name,
  });

  const mapCodeOrNameOption = (entry: { id: number; name?: string; code?: string; symbol?: string }): SelectOption => ({
    value: entry.id,
    label: entry.name || entry.code || entry.symbol || `ID ${entry.id}`,
  });

  const createProduct = useMutation({
    mutationFn: (payload: Record<string, any>) => api.post('warehouses/api/products/', payload),
    onSuccess: () => {
      navigate('/warehouses/products');
    },
    onError: (requestError: any) => {
      const data = requestError?.response?.data;
      if (data && typeof data === 'object') {
        const firstErrorKey = Object.keys(data)[0];
        const firstErrorVal = data[firstErrorKey];
        const errorMsg = Array.isArray(firstErrorVal) ? firstErrorVal[0] : firstErrorVal;
        setError(`${firstErrorKey}: ${errorMsg}`);
      } else {
        setError(t('warehouses.products.createError'));
      }
    },
  });

  // Výpočet z Ceny bez DPH -> Cena s DPH
  const handlePricePurchaseChange = (val: string) => {
    const ratePercent = defaultTax?.rate ? parseFloat(defaultTax.rate) : 0;

    if (val !== '' && !isNaN(parseFloat(val)) && ratePercent > 0) {
      const basePrice = parseFloat(val);
      const calculatedVat = (basePrice * (1 + ratePercent / 100)).toFixed(2);
      setForm((prev) => ({ ...prev, price_purchase: val, price_purchase_vat: calculatedVat }));
    } else {
      setForm((prev) => ({ ...prev, price_purchase: val, price_purchase_vat: val === '' ? '' : prev.price_purchase_vat }));
    }
  };

  // Výpočet z Ceny s DPH -> Cena bez DPH
  const handlePricePurchaseVatChange = (val: string) => {
    const ratePercent = defaultTax?.rate ? parseFloat(defaultTax.rate) : 0;

    if (val !== '' && !isNaN(parseFloat(val)) && ratePercent > 0) {
      const vatPrice = parseFloat(val);
      const calculatedBase = (vatPrice / (1 + ratePercent / 100)).toFixed(2);
      setForm((prev) => ({ ...prev, price_purchase_vat: val, price_purchase: calculatedBase }));
    } else {
      setForm((prev) => ({ ...prev, price_purchase_vat: val, price_purchase: val === '' ? '' : prev.price_purchase }));
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name || !form.sku || !form.default_daily_rate) {
      setError(t('warehouses.products.requiredFields'));
      return;
    }

    setError(null);

    const payload: Record<string, any> = {
      name: form.name,
      sku: form.sku,
      description: form.description,
      color: form.color,
      tracking_type: form.tracking_type,
      priority: Number(form.priority) || 0,
      weight: parseFloat(form.weight) || 0,
      default_daily_rate: parseFloat(form.default_daily_rate),
      exclude_from_discount: form.exclude_from_discount,
      category: form.category ? Number(form.category) : null,
      product_type: form.product_type ? Number(form.product_type) : null,
      weight_unit: form.weight_unit ? Number(form.weight_unit) : null,
      currency: form.currency ? Number(form.currency) : null,
      price_purchase: form.price_purchase !== '' ? parseFloat(form.price_purchase) : null,
      price_purchase_vat: form.price_purchase_vat !== '' ? parseFloat(form.price_purchase_vat) : null,
      ...buildProductDimensionsPayload(form),
    };

    createProduct.mutate(payload);
  };

  const isSubmitDisabled = !form.name || !form.sku || !form.default_daily_rate || createProduct.isPending;

  return (
    <PageShell
      pretitle={t('warehouses.products.catalog')}
      title={t('warehouses.products.add')}
      description={t('warehouses.products.addDescription')}
      actions={
        <Button variant="outline-secondary" onClick={() => navigate('/warehouses/products')}>
          <IconArrowLeft size={16} className="me-1" />
          {t('common.back')}
        </Button>
      }
    >
      <PageForm
        title={t('warehouses.products.productCard')}
        onSubmit={submit}
        footer={
          <>
            <Button variant="outline-secondary" onClick={() => navigate('/warehouses/products')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              <IconPlus size={16} className="me-1" />
              {t('warehouses.products.save')}
            </Button>
          </>
        }
      >
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {/* --- 1. ZÁKLADNÍ IDENTIFIKACE --- */}
        <h5 className="mb-3 text-muted">{t('warehouses.products.basicInfo')}</h5>
        <Row className="g-3 mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.name')} *</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('warehouses.products.namePlaceholder')}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>SKU *</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('warehouses.products.skuPlaceholder')}
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.categories.title')}</Form.Label>
              <PaginatedAsyncSelect
                value={form.category_option}
                onChange={(opt: SelectOption | null) =>
                  setForm({ ...form, category: opt?.value || '', category_option: opt })
                }
                endpoint="warehouses/api/product-categories/"
                mapOption={mapNamedOption}
                placeholder={t('warehouses.products.selectCategory')}
                noOptionsMessage={() => t('warehouses.products.categoryNotFound')}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.productType')}</Form.Label>
              <PaginatedAsyncSelect
                value={form.product_type_option}
                onChange={(opt: SelectOption | null) =>
                  setForm({ ...form, product_type: opt?.value || '', product_type_option: opt })
                }
                endpoint="warehouses/api/product-types/"
                mapOption={mapNamedOption}
                placeholder={t('warehouses.products.selectProductType')}
                noOptionsMessage={() => t('warehouses.products.typeNotFound')}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>{t('warehouses.items.description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={t('warehouses.products.descriptionPlaceholder')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Form.Group>

        <hr className="my-4" />

        {/* --- 2. KLASIFIKACE & VZHLED --- */}
        <h5 className="mb-3 text-muted">{t('warehouses.products.settingsAndClassification')}</h5>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.trackingType')}</Form.Label>
              <Form.Select
                value={form.tracking_type}
                onChange={(e) => setForm({ ...form, tracking_type: e.target.value })}
              >
                <option value="SERIALIZED">{t('warehouses.products.trackingSerialized')}</option>
                <option value="BULK">{t('warehouses.products.trackingBulk')}</option>
                <option value="CONSUMABLE">{t('warehouses.products.trackingConsumable')}</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.priority')}</Form.Label>
              <NumberInput
                step="any"
                value={form.priority}
                onChange={(priority) => setForm({ ...form, priority })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.color')}</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  style={{ width: '50px', height: '38px', padding: '2px' }}
                />
                <Form.Control
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-4" />

        {/* --- 3. FYZICKÉ VLASTNOSTI --- */}
        <h5 className="mb-3 text-muted">{t('warehouses.products.physicalProperties')}</h5>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.weight')}</Form.Label>
              <Form.Control
                type="number"
                step="any"
                min="0"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.weightUnit')}</Form.Label>
              <PaginatedAsyncSelect
                value={form.weight_unit_option}
                onChange={(opt: SelectOption | null) =>
                  setForm({ ...form, weight_unit: opt?.value || '', weight_unit_option: opt })
                }
                endpoint="settings/api/weight-units/"
                mapOption={mapCodeOrNameOption}
                placeholder={t('warehouses.products.selectUnit')}
                noOptionsMessage={() => t('warehouses.products.unitNotFound')}
              />
            </Form.Group>
          </Col>
        </Row>
        <ProductDimensionsFields value={form} onChange={(patch) => setForm({ ...form, ...patch })} />

        <hr className="my-4" />

        {/* --- 4. FINANČNÍ ÚDAJE A TARIF --- */}
        <h5 className="mb-3 text-muted">{t('warehouses.products.financeAndTariff')}</h5>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.defaultDailyRate')} *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                min="0"
                placeholder={t('warehouses.products.defaultDailyRatePlaceholder')}
                value={form.default_daily_rate}
                onChange={(e) => setForm({ ...form, default_daily_rate: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                {t('warehouses.products.defaultDailyRateHelp')}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{t('warehouses.items.currency')}</Form.Label>
              <PaginatedAsyncSelect
                value={form.currency_option}
                onChange={(opt: SelectOption | null) =>
                  setForm({ ...form, currency: opt?.value || '', currency_option: opt })
                }
                endpoint="settings/api/currency/"
                mapOption={mapCodeOrNameOption}
                placeholder={t('warehouses.products.defaultCurrency')}
                noOptionsMessage={() => t('warehouses.products.currencyNotFound')}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>{t('warehouses.products.purchasePrice')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.00"
                  value={form.price_purchase}
                  onChange={(e) => handlePricePurchaseChange(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                {t('warehouses.products.purchasePriceVat')}
                {defaultTax?.rate && <small className="text-muted ms-1">({parseFloat(defaultTax.rate)}%)</small>}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.00"
                  value={form.price_purchase_vat}
                  onChange={(e) => handlePricePurchaseVatChange(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-center mt-4">
            <Form.Group>
              <Form.Check
                type="checkbox"
                id="exclude_from_discount"
                label={t('warehouses.products.excludeFromDiscount')}
                checked={form.exclude_from_discount}
                onChange={(e) => setForm({ ...form, exclude_from_discount: e.target.checked })}
              />
            </Form.Group>
          </Col>
        </Row>
      </PageForm>
    </PageShell>
  );
}
